import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import api, { formatApiError } from '../../services/api';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';
import {
  CATEGORY_COLOR_PALETTE,
  DEFAULT_CATEGORY_HEX,
  hexToRgb,
  normalizeHex,
  resolveCategoryHex,
  rgbToHex,
} from '../planning/colorUtils';

const emptyForm = () => ({
  code: '',
  label: '',
  color: DEFAULT_CATEGORY_HEX,
  is_active: true,
});

function CategoryColorPicker({ idPrefix, value, onChange }) {
  const { t } = useTranslation(['planning', 'common']);
  const hex = resolveCategoryHex(value);
  const rgb = useMemo(() => hexToRgb(hex), [hex]);
  const [rgbDraft, setRgbDraft] = useState({
    r: String(rgb.r),
    g: String(rgb.g),
    b: String(rgb.b),
  });

  useEffect(() => {
    setRgbDraft({
      r: String(rgb.r),
      g: String(rgb.g),
      b: String(rgb.b),
    });
  }, [rgb.r, rgb.g, rgb.b]);

  const commitRgb = (next) => {
    const r = Math.max(0, Math.min(255, parseInt(next.r, 10) || 0));
    const g = Math.max(0, Math.min(255, parseInt(next.g, 10) || 0));
    const b = Math.max(0, Math.min(255, parseInt(next.b, 10) || 0));
    onChange(rgbToHex(r, g, b));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor={`${idPrefix}-color`}>
        {t('colColor')}
      </label>
      <p className="text-xs text-slate-500 mb-2" id={`${idPrefix}-color-help`}>
        {t('colorHelp')}
      </p>

      <div className="grid grid-cols-8 gap-1.5 mb-3" role="listbox" aria-labelledby={`${idPrefix}-color-help`}>
        {CATEGORY_COLOR_PALETTE.map((swatch) => {
          const selected = normalizeHex(swatch) === hex;
          return (
            <button
              key={swatch}
              type="button"
              title={swatch}
              onClick={() => onChange(normalizeHex(swatch))}
              className={`h-7 w-full rounded-md border transition-all ${
                selected
                  ? 'ring-2 ring-primary ring-offset-1 border-primary'
                  : 'border-slate-200 hover:scale-105'
              }`}
              style={{ backgroundColor: swatch }}
              aria-label={t('pickSwatch', { swatch })}
              aria-pressed={selected}
            />
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <input
            id={`${idPrefix}-color`}
            type="color"
            value={hex.toLowerCase()}
            onChange={(e) => onChange(normalizeHex(e.target.value))}
            className="h-10 w-12 cursor-pointer rounded border border-slate-200 bg-white p-1"
            title={t('customRgbColor')}
            aria-describedby={`${idPrefix}-color-help ${idPrefix}-color-hint`}
          />
          <span
            className="inline-block h-8 w-8 rounded-full border border-slate-200 shadow-sm"
            style={{ backgroundColor: hex }}
            aria-hidden="true"
          />
        </div>

        <div className="flex items-center gap-1.5">
          {(['r', 'g', 'b']).map((channel) => (
            <label key={channel} className="flex flex-col items-center gap-0.5">
              <span className="text-[10px] font-semibold uppercase text-slate-500">{channel}</span>
              <input
                type="number"
                min={0}
                max={255}
                value={rgbDraft[channel]}
                onChange={(e) => {
                  const next = { ...rgbDraft, [channel]: e.target.value };
                  setRgbDraft(next);
                }}
                onBlur={() => commitRgb(rgbDraft)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitRgb(rgbDraft);
                  }
                }}
                className="w-14 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-center text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
              />
            </label>
          ))}
        </div>

        <input
          type="text"
          value={hex}
          onChange={(e) => {
            const raw = e.target.value.trim();
            if (/^#[0-9A-Fa-f]{0,6}$/.test(raw)) {
              onChange(raw.length === 7 ? normalizeHex(raw) : raw.toUpperCase());
            }
          }}
          onBlur={() => onChange(normalizeHex(hex))}
          className="w-28 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 font-mono text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
          placeholder="#124191"
          aria-label={t('hexColor')}
        />
      </div>
      <p className="mt-1.5 text-xs text-slate-500" id={`${idPrefix}-color-hint`}>
        {t('colorHint')}
      </p>
    </div>
  );
}

const CategoriesConfigModal = ({ open, onClose, onChanged }) => {
  const { t } = useTranslation(['planning', 'common']);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm());
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [editModal, setEditModal] = useState({ isOpen: false, category: null });
  const [editForm, setEditForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    categoryId: null,
    categoryLabel: null,
    eventCount: 0,
  });
  const [deleteTargetId, setDeleteTargetId] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const { toast, showSuccess, showError, hideToast } = useToast();

  const notifyChanged = () => {
    onChanged?.();
  };

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getCategories();
      const list = Array.isArray(response) ? response : [];
      setCategories(list);
    } catch (err) {
      const formatted = formatApiError(err);
      setError(formatted.generalError || t('failedToLoadCategories'));
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    fetchCategories();
    return undefined;
  }, [open]);

  const filtered = categories.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (
      String(c.code || '').toLowerCase().includes(q) ||
      String(c.label || '').toLowerCase().includes(q)
    );
  });

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    setAddError(null);
    try {
      await api.createCategory({
        code: addForm.code.trim(),
        label: addForm.label.trim(),
        color: normalizeHex(addForm.color),
        is_active: !!addForm.is_active,
      });
      showSuccess(t('categoryCreated'), t('categoryCreatedDetail', { label: addForm.label }));
      setShowAddModal(false);
      setAddForm(emptyForm());
      await fetchCategories();
      notifyChanged();
    } catch (err) {
      const formatted = formatApiError(err);
      setAddError(formatted.generalError || t('failedToCreateCategory'));
      showError(t('createFailed'), formatted.generalError || t('failedToCreateCategory'));
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (category) => {
    setEditForm({
      code: category.code || '',
      label: category.label || '',
      color: resolveCategoryHex(category.color),
      is_active: category.is_active !== false,
    });
    setSaveError(null);
    setEditModal({ isOpen: true, category });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editModal.category) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.updateCategory(editModal.category.id, {
        code: editForm.code.trim(),
        label: editForm.label.trim(),
        color: normalizeHex(editForm.color),
        is_active: !!editForm.is_active,
      });
      showSuccess(t('categoryUpdated'), t('categoryUpdatedDetail', { label: editForm.label }));
      setEditModal({ isOpen: false, category: null });
      await fetchCategories();
      notifyChanged();
    } catch (err) {
      const formatted = formatApiError(err);
      setSaveError(formatted.generalError || t('failedToUpdateCategory'));
      showError(t('updateFailed'), formatted.generalError || t('failedToUpdateCategory'));
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (category) => {
    setDeleteError(null);
    setDeleteTargetId('');
    setDeleteModal({
      isOpen: true,
      categoryId: category.id,
      categoryLabel: category.label || category.code,
      eventCount: category.event_count || 0,
    });
  };

  const handleDeleteConfirm = async () => {
    const { categoryId, categoryLabel, eventCount } = deleteModal;
    if (eventCount > 0 && (!deleteTargetId || deleteTargetId === '')) {
      setDeleteError(t('selectReassignCategory'));
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      const options =
        eventCount > 0 && deleteTargetId
          ? { target_category_id: parseInt(deleteTargetId, 10) }
          : {};
      await api.deleteCategory(categoryId, options);
      const detail =
        eventCount > 0
          ? `${t('categoryDeletedDetail', { label: categoryLabel })} ${t('eventsReassigned', { count: eventCount })}`
          : t('categoryDeletedDetail', { label: categoryLabel });
      showSuccess(t('categoryDeleted'), detail);
      setDeleteModal({ isOpen: false, categoryId: null, categoryLabel: null, eventCount: 0 });
      setDeleteTargetId('');
      await fetchCategories();
      notifyChanged();
    } catch (err) {
      const formatted = formatApiError(err);
      setDeleteError(formatted.generalError || t('failedToDeleteCategory'));
      showError(t('deleteFailed'), formatted.generalError || t('failedToDeleteCategory'));
    } finally {
      setDeleting(false);
    }
  };

  const otherCategoriesForDelete = categories.filter(
    (c) => c.id !== deleteModal.categoryId
  );

  const renderFormFields = (form, setForm, idPrefix) => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor={`${idPrefix}-code`}>
          {t('colCode')}
        </label>
        <input
          id={`${idPrefix}-code`}
          type="text"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
          className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
          placeholder={t('codePlaceholder')}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor={`${idPrefix}-label`}>
          {t('colLabel')}
        </label>
        <input
          id={`${idPrefix}-label`}
          type="text"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
          placeholder={t('displayName')}
          required
        />
      </div>
      <CategoryColorPicker
        idPrefix={idPrefix}
        value={form.color}
        onChange={(color) => setForm({ ...form, color })}
      />
      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.is_active}
          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          className="rounded border-slate-300 text-primary focus:ring-primary"
        />
        {t('categoryActiveLabel')}
      </label>
    </div>
  );

  const nestedBusy = adding || saving || deleting;

  const handleClose = () => {
    if (nestedBusy) return;
    if (showAddModal || editModal.isOpen || deleteModal.isOpen) return;
    onClose?.();
  };

  return (
    <AnimatePresence>
      {open ? (
        <div className="teams-planning-page categories-config-root" key="categories-config">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-slate-900/40"
          />
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            onClick={handleClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-4xl border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="categories-config-title"
            >
              <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 shrink-0">
                <h2 id="categories-config-title" className="text-lg font-bold text-slate-900">
                  {t('eventCategories')}
                </h2>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                  aria-label={t('common:close')}
                  title={t('common:close')}
                >
                  <span className="material-symbols-outlined text-[22px]">close</span>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="relative flex-1 max-w-md">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
                      search
                    </span>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('searchByCodeOrLabel')}
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAddForm(emptyForm());
                      setAddError(null);
                      setShowAddModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    {t('addCategory')}
                  </button>
                </div>

                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                        <tr>
                          <th className="px-4 py-3 font-semibold">{t('colLabel')}</th>
                          <th className="px-4 py-3 font-semibold">{t('colCode')}</th>
                          <th className="px-4 py-3 font-semibold">{t('colColor')}</th>
                          <th className="px-4 py-3 font-semibold">{t('colEvents')}</th>
                          <th className="px-4 py-3 font-semibold">{t('common:status')}</th>
                          <th className="px-4 py-3 font-semibold text-right">{t('common:actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                              {t('loadingCategories')}
                            </td>
                          </tr>
                        ) : filtered.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                              {t('noCategoriesFound')}
                            </td>
                          </tr>
                        ) : (
                          filtered.map((category) => {
                            const hex = resolveCategoryHex(category.color);
                            return (
                              <tr key={category.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                                <td className="px-4 py-3 font-medium text-slate-900">{category.label}</td>
                                <td className="px-4 py-3 font-mono text-slate-700">{category.code}</td>
                                <td className="px-4 py-3">
                                  <span className="inline-flex items-center gap-2">
                                    <span
                                      className="h-3 w-3 rounded-full border border-slate-200"
                                      style={{ backgroundColor: hex }}
                                    />
                                    <span className="font-mono text-xs text-slate-600">{hex}</span>
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-slate-600">{category.event_count ?? 0}</td>
                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                                      category.is_active
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-slate-100 text-slate-600'
                                    }`}
                                  >
                                    {category.is_active ? t('common:active') : t('common:inactive')}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="inline-flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => openEdit(category)}
                                      className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-primary"
                                      title={t('common:edit')}
                                    >
                                      <span className="material-symbols-outlined text-[18px]">edit</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openDelete(category)}
                                      className="p-1.5 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600"
                                      title={t('common:delete')}
                                    >
                                      <span className="material-symbols-outlined text-[18px]">delete_outline</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <AnimatePresence>
            {showAddModal && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-[10050]"
                  onClick={() => !adding && setShowAddModal(false)}
                />
                <div className="fixed inset-0 z-[10051] flex items-center justify-center p-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-white rounded-xl shadow-xl max-w-lg w-full border border-slate-200 max-h-[90vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <form onSubmit={handleAdd} className="p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">{t('addCategory')}</h3>
                      {addError && (
                        <p className="text-sm text-red-600 mb-3">{addError}</p>
                      )}
                      {renderFormFields(addForm, setAddForm, 'add')}
                      <div className="flex justify-end gap-3 mt-6">
                        <button
                          type="button"
                          onClick={() => setShowAddModal(false)}
                          disabled={adding}
                          className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
                        >
                          {t('common:cancel')}
                        </button>
                        <button
                          type="submit"
                          disabled={adding}
                          className="px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg disabled:opacity-60"
                        >
                          {adding ? t('creating') : t('common:create')}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                </div>
              </>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {editModal.isOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-[10050]"
                  onClick={() => !saving && setEditModal({ isOpen: false, category: null })}
                />
                <div className="fixed inset-0 z-[10051] flex items-center justify-center p-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-white rounded-xl shadow-xl max-w-lg w-full border border-slate-200 max-h-[90vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <form onSubmit={handleSave} className="p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">{t('editCategory')}</h3>
                      {saveError && (
                        <p className="text-sm text-red-600 mb-3">{saveError}</p>
                      )}
                      {renderFormFields(editForm, setEditForm, 'edit')}
                      <div className="flex justify-end gap-3 mt-6">
                        <button
                          type="button"
                          onClick={() => setEditModal({ isOpen: false, category: null })}
                          disabled={saving}
                          className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
                        >
                          {t('common:cancel')}
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg disabled:opacity-60"
                        >
                          {saving ? t('common:saving') : t('common:save')}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                </div>
              </>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {deleteModal.isOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-[10050]"
                  onClick={() =>
                    !deleting &&
                    setDeleteModal({ isOpen: false, categoryId: null, categoryLabel: null, eventCount: 0 })
                  }
                />
                <div className="fixed inset-0 z-[10051] flex items-center justify-center p-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-white rounded-xl shadow-xl max-w-md w-full border border-slate-200"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                          <span className="material-symbols-outlined text-red-600 text-[24px]">warning</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-slate-900 mb-2">{t('deleteCategory')}</h3>
                          {deleteModal.eventCount > 0 ? (
                            <>
                              <p className="text-sm text-slate-600 mb-2">
                                {t('deleteCategoryHasEvents', { count: deleteModal.eventCount })}
                              </p>
                              {deleteError && (
                                <p className="text-sm text-red-600 font-medium mb-2">{deleteError}</p>
                              )}
                              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                {t('reassignEventsTo')}
                              </label>
                              <select
                                value={deleteTargetId}
                                onChange={(e) => {
                                  setDeleteTargetId(e.target.value);
                                  setDeleteError(null);
                                }}
                                className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                              >
                                <option value="">{t('selectCategory')}</option>
                                {otherCategoriesForDelete.map((c) => (
                                  <option key={c.id} value={c.id.toString()}>
                                    {c.label} ({c.code})
                                  </option>
                                ))}
                              </select>
                              {otherCategoriesForDelete.length === 0 && (
                                <p className="text-sm text-amber-600 mt-1">
                                  {t('noOtherCategoryAvailable')}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-slate-600 mb-1">
                                {t('confirmDeleteCategory', { label: deleteModal.categoryLabel })}
                              </p>
                              <p className="text-sm text-red-600 font-medium">
                                {t('cannotUndo')}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-3 mt-6">
                        <button
                          type="button"
                          onClick={() =>
                            setDeleteModal({
                              isOpen: false,
                              categoryId: null,
                              categoryLabel: null,
                              eventCount: 0,
                            })
                          }
                          disabled={deleting}
                          className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
                        >
                          {t('common:cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={handleDeleteConfirm}
                          disabled={
                            deleting ||
                            (deleteModal.eventCount > 0 && otherCategoriesForDelete.length === 0)
                          }
                          className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-60"
                        >
                          {deleting ? t('deleting') : t('common:delete')}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </>
            )}
          </AnimatePresence>

          <Toast toast={toast} onClose={hideToast} />
        </div>
      ) : null}
    </AnimatePresence>
  );
};

/** Icon button matching people-filters-toggle; opens categories config. */
export function TeamsPlanningCategoriesButton({ onClick }) {
  const { t } = useTranslation(['planning', 'common']);
  return (
    <div className="teams-planning-page">
      <button
        type="button"
        className="people-filters-toggle categories-config-toggle"
        onClick={onClick}
        aria-label={t('manageEventCategories')}
        title={t('eventCategories')}
      >
        <span className="material-symbols-outlined">category</span>
      </button>
    </div>
  );
}

export default CategoriesConfigModal;
